<!-- 
Panel for choosing an MFA protection option
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog sm">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>Setup authenticator app (multi-factor authentication)</v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="common" section="mfa-account-protection"/>
        </v-toolbar>
        <our-busy-indicator :show="busy" />
        <v-card-text>
          <v-container fluid class="pa-0">
            <p class="subheading">
              1. Install an authenticator app
            </p>
            <p>
              Install an authenticator app on your mobile device. We recommend Google Authenticator or Microsoft Authenticator
            </p>
            <p class="subheading">
              2. Scan the QR code
            </p>
            <v-flex offset-xs1>
              <v-card 
              flat 
              tile 
              class="d-flex" 
              height="250"
              width="250"
              >
                <v-img
                  :key="qrCodeImageId"
                  :src="qrCodeImageSrc"
                  aspect-ratio="1"
                  class="grey lighten-2"
                  alt="QR Code Image for Authenticator App"
                >
                  <template v-slot:placeholder>
                    <v-layout
                      fill-height
                      align-center
                      justify-center
                      ma-0
                    >
                      <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                    </v-layout>
                  </template>
                </v-img>
              </v-card>
            </v-flex>
            <p>If you have problems with the QR code, setup using the <a class="i-link" @click.stop.prevent="showManualCode=!showManualCode">manual code.</a></p>
            <v-alert
              :value="showManualCode"
              color="info"
              icon="info"
              outline
            >
            {{ this.manualCode }}
            </v-alert>
            <v-flex xs12>
              <p class="subheading">
                3. Enter the code
              </p>
            </v-flex>
            <v-flex xs12 md4 sm6 offset-xs1>
              <v-text-field
                  v-model="oneTimePassword"
                  :rules="[rules.required, rules.number, rules.greaterThanZero]"
                  autofocus persistent-hint
                  label="Code" hint="Code from the Authenticator App"
                  required
                />
              </v-flex>
        </v-container>
      </v-card-text>
        <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" show-security-errors />
        <v-card-actions>
            <v-spacer/>
            <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
            <v-btn 
              ref="save-button" :disabled="busy" flat color="success" 
              type="submit" @click.prevent="save"
            >Setup</v-btn>
          </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import * as action from "@/store/prisms-users/action-types";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import _ from "underscore";
import { imageHelper } from "@/helpers/image.helper";

export default {
  mixins: [ruleMixins,lookupMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showValidationDialog: false,
      showManualCode: false,
      qrCodeBase64: null,
      manualCode: null,
      oneTimePassword: "",
      qrCodeImageId: `qrCodeImageId-${Date.now()}`
    };
  },
  computed: {
    state() {
      return this.$store.state.prismsUsers.setupAuthenticatorMfa;
    },
    busy() {
      return this.state.loading || this.state.saving;
    },
    qrCodeImageLoaded() {
      return this.qrCodeBase64 !== null;
    },
    qrCodeImageSrc() {
      return this.qrCodeImageLoaded ? imageHelper.srcFromBase64(this.qrCodeBase64) : "";
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        await this.getAuthenticatorAppSetup();
      }
      else {
        this.reset();
      }
    }
  },
  methods: {
    async getAuthenticatorAppSetup() {
      if (await this.$store.dispatch(action.SETUP_AUTHENTICATOR_APP_MFA_GET)) {
        this.qrCodeBase64 = this.state.get.data.qrCodeInBase64;
        this.manualCode = this.state.get.data.secretInBase32;
      }
      else {
        this.qrCodeBase64 = null;
      }
    },
    async save() {
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.SETUP_AUTHENTICATOR_APP_MFA, { oneTimePassword: this.oneTimePassword, secretInBase32: this.state.setup.data.secretInBase32 }))) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.reset();
      this.$emit("update:show", false);
    },
    reset() {
      this.showValidationDialog = false,
      this.qrCodeBase64 = null,
      this.oneTimePassword = "";
      this.manualCode = null;
      this.showManualCode = false;
      this.qrCodeImageId = `qrCodeImageId-${Date.now()}`;
      this.$refs.form.reset();
    }
  }
};
</script>
